import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { useKeyboard } from '@/client/hooks/use-keyboard';

export function DeleteConfirmationModal() {
  const { deleteConfirmation, closeDeleteConfirmation } = useUIStore();
  const [confirmText, setConfirmText] = useState('');
  const queryClient = useQueryClient();

  const deleteType = deleteConfirmation?.type;
  const requiresConfirmation = deleteType === 'user';

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deleteConfirmation) throw new Error('No item to delete');

      const endpoints: Record<string, string> = {
        link: `/api/links/${deleteConfirmation.id}`,
        token: `/api/tokens/${deleteConfirmation.id}`,
        user: `/api/admin/users/${deleteConfirmation.id}`,
        'qr-code': `/api/qr-codes/${deleteConfirmation.id}`,
        page: `/api/landing-pages/${deleteConfirmation.id}`,
        form: `/api/forms/${deleteConfirmation.id}`,
      };

      const endpoint = endpoints[deleteConfirmation.type];
      if (!endpoint) throw new Error('Invalid delete type');

      const response = await fetch(endpoint, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete');
      }

      return response.json();
    },
    onSuccess: () => {
      const labels: Record<string, string> = {
        link: 'Link',
        token: 'API token',
        user: 'User',
        'qr-code': 'QR code',
        page: 'Page',
        form: 'Form',
      };
      toast.success(`${labels[deleteConfirmation?.type || 'link']} deleted successfully`);

      // Invalidate relevant queries
      if (deleteConfirmation?.type === 'link') {
        queryClient.invalidateQueries({ queryKey: ['links'] });
      } else if (deleteConfirmation?.type === 'token') {
        queryClient.invalidateQueries({ queryKey: ['tokens'] });
      } else if (deleteConfirmation?.type === 'user') {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      } else if (deleteConfirmation?.type === 'qr-code') {
        queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      } else if (deleteConfirmation?.type === 'page') {
        queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      } else if (deleteConfirmation?.type === 'form') {
        queryClient.invalidateQueries({ queryKey: ['forms'] });
      }

      closeDeleteConfirmation();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Reset confirm text when modal closes
  useEffect(() => {
    if (!deleteConfirmation) {
      setConfirmText('');
    }
  }, [deleteConfirmation]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', closeDeleteConfirmation, !!deleteConfirmation);

  if (!deleteConfirmation) return null;

  const canDelete = !requiresConfirmation || confirmText === deleteConfirmation.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeDeleteConfirmation}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b">
          <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Delete {deleteConfirmation.type}</h2>
            <p className="text-sm text-muted-foreground">This action cannot be undone</p>
          </div>
          <button
            onClick={closeDeleteConfirmation}
            className="ml-auto p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close delete confirmation modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p>
            Are you sure you want to delete{' '}
            <span className="font-medium">{deleteConfirmation.name}</span>?
          </p>

          {deleteType === 'link' && (
            <p className="text-sm text-muted-foreground">
              All analytics data for this link will be permanently deleted.
            </p>
          )}

          {deleteType === 'token' && (
            <p className="text-sm text-muted-foreground">
              Any applications using this token will lose access immediately.
            </p>
          )}

          {deleteType === 'qr-code' && (
            <p className="text-sm text-muted-foreground">
              All scan analytics for this QR code will be permanently deleted.
            </p>
          )}

          {deleteType === 'page' && (
            <p className="text-sm text-muted-foreground">
              This page will be unpublished and all view analytics will be permanently deleted.
            </p>
          )}

          {deleteType === 'form' && (
            <p className="text-sm text-muted-foreground">
              This form and all its responses will be permanently deleted.
            </p>
          )}

          {deleteType === 'user' && (
            <>
              <p className="text-sm text-muted-foreground">
                All links and data associated with this user will be permanently deleted.
              </p>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Type{' '}
                  <span className="font-mono bg-muted px-1 rounded">{deleteConfirmation.name}</span>{' '}
                  to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-destructive"
                  placeholder="Enter name to confirm"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={closeDeleteConfirmation}
            className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={!canDelete || deleteMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground shadow hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
